import { useLayoutEffect, useRef, type ReactNode } from "react";
import { motion, useMotionValue, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { parallaxOffset, type LayerBox } from "./parallaxOffset";

/**
 * Décor qui glisse au défilement, à une autre vitesse que la page. Sur la
 * traversée de la fenêtre, il se décale de `distance` px : positive, il
 * reste en retrait et va moins vite que la page ; négative, il passe au
 * premier plan. Le cadre reste en place ; seul son contenu glisse. Sa
 * position est mesurée à l'arrivée et quand la page change de taille, pas à
 * chaque image. Sans mouvement : immobile.
 */
export function ParallaxLayer({
  distance = 40,
  className,
  children,
}: {
  distance?: number;
  className?: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();
  const { scrollY } = useScroll();
  const box = useMotionValue<LayerBox>({ top: 0, height: 0, viewport: 0 });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || reduce) return;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      box.set({ top: rect.top + window.scrollY, height: rect.height, viewport: window.innerHeight });
    };
    measure();
    window.addEventListener("resize", measure);
    // Une FAQ qui s'ouvre ou une police qui arrive déplace le décor.
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(measure) : null;
    observer?.observe(document.body);
    return () => {
      window.removeEventListener("resize", measure);
      observer?.disconnect();
    };
  }, [box, reduce]);

  const y = useTransform(() => (reduce ? 0 : parallaxOffset(scrollY.get(), box.get(), distance)));
  return (
    <div ref={ref} data-parallax={distance} aria-hidden="true" className={className}>
      <motion.div className="h-full w-full" style={{ y }}>
        {children}
      </motion.div>
    </div>
  );
}
